import React, { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { RootState } from '../store';
import { updateUserProfile } from '../store/slices/authSlice';
import { getApiUrl } from '../utils/api';
import { User, Mail, Save, Loader2 } from 'lucide-react';
import { HelpIcon } from '../components/HelpIcon';

export function ProfilePage() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { user } = useSelector((state: RootState) => state.auth);
  const [loading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  
  const [formData, setFormData] = useState({
    name: '',
    email: ''
  });

  useEffect(() => {
    if (user) {
      setFormData({
        name: user.name || '',
        email: user.email || ''
      });
    }
  }, [user]);

  const handleInputChange = (field: 'name' | 'email', value: string) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
    // Clear messages when user starts typing
    if (error) setError('');
    if (success) setSuccess('');
  };

  const validateForm = (): boolean => {
    if (!formData.name.trim()) {
      setError(t('settings.profile.validation.nameRequired'));
      return false;
    }
    
    // Only validate email if user is super admin (can edit email)
    if (user?.role === 'super_admin') {
      if (!formData.email.trim()) {
        setError(t('settings.profile.validation.emailRequired'));
        return false;
      }

      // Basic email validation
      const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailRegex.test(formData.email)) {
        setError(t('settings.profile.validation.emailInvalid'));
        return false;
      }
    }

    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }

    if (!user) {
      setError('Utilisateur non trouvé');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const token = localStorage.getItem('token');
      
      // Prepare update payload based on user role
      const updatePayload: { name: string; email?: string } = {
        name: formData.name
      };
      
      // Only include email if user is super admin
      if (user.role === 'super_admin') {
        updatePayload.email = formData.email;
      }

      const response = await fetch(getApiUrl(`/api/admin/admins/${user.id}`), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(updatePayload)
      });

      if (response.ok) {
        const data = await response.json();
        const updatedAdmin = data.data.admin;
        
        // Update Redux store with new user data
        dispatch(updateUserProfile({
          name: updatedAdmin.name,
          email: updatedAdmin.email
        }));
        
        setSuccess(t('settings.profile.success'));
      } else {
        const errorData = await response.json();
        setError(errorData.message || t('settings.profile.error'));
      }
    } catch (error) {
      console.error('Profile update error:', error);
      setError(t('settings.profile.error'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="h-8 w-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-display font-bold text-textPrimary flex items-center space-x-3">
          <User className="w-8 h-8 text-primary-600" />
          <span>{t('settings.profile.title')}</span>
          <HelpIcon 
            docPath="16-parametres#16-3-mon-profil" 
            tooltip="Documentation : Mon Profil"
          />
        </h1>
        <p className="text-textSecondary mt-2">
          {t('settings.profile.subtitle')}
        </p>
      </div>

      {/* Success/Error Messages */}
      {success && (
        <div className="bg-green-50 border border-green-200 text-green-800 px-4 py-3 rounded-lg">
          {success}
        </div>
      )}
      
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded-lg">
          {error}
        </div>
      )}

      {/* Profile Form */}
      <form onSubmit={handleSubmit} className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-6">
        {/* Name Field */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
            {t('settings.profile.name')}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <User className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500"
              placeholder={t('settings.profile.namePlaceholder')}
              required
            />
          </div>
        </div>

        {/* Email Field - Only editable for super admins */}
        <div>
          <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
            {t('settings.profile.email')}
            {user?.role !== 'super_admin' && (
              <span className="ml-2 text-xs text-gray-500">{t('settings.profile.emailNotEditable')}</span>
            )}
          </label>
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Mail className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="email"
              id="email"
              value={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              disabled={user?.role !== 'super_admin'}
              className={`block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-primary-500 focus:border-primary-500 ${
                user?.role !== 'super_admin' ? 'bg-gray-50 cursor-not-allowed' : ''
              }`}
              placeholder={t('settings.profile.emailPlaceholder')}
              required
            />
          </div>
        </div>

        {/* Role Display (Read-only) */}
        {user && (
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t('settings.profile.role')}
            </label>
            <div className="px-3 py-2 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
              {user.role === 'super_admin' ? t('settings.profile.roles.super_admin') : 
               user.role === 'admin' ? t('settings.profile.roles.admin') : 
               user.role}
            </div>
          </div>
        )}

        {/* Submit Button */}
        <div className="flex justify-end pt-4 border-t border-gray-200">
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                {t('settings.profile.saving')}
              </>
            ) : (
              <>
                <Save className="mr-2 h-4 w-4" />
                {t('settings.profile.save')}
              </>
            )}
          </button>
        </div>
      </form>

      {/* Info Section */}
      <div className="bg-gray-50 rounded-lg border border-gray-200 p-4">
        <p className="text-sm text-gray-600">
          {user?.role === 'super_admin' ? (
            t('settings.profile.note.superAdmin')
          ) : (
            t('settings.profile.note.regular')
          )}
        </p>
      </div>
    </div>
  );
}

